import { NavLink, Navigate, Outlet, Link } from "react-router-dom";
import { LayoutDashboard, Package, ShoppingCart, Users, Tag, Truck, Settings, Inbox, LogOut, Store } from "lucide-react";
import { useStore } from "../context/StoreContext";
import { api } from "../lib/api";
import { AuthForm } from "../pages/Account";
import { Seo } from "../components/site/Seo";

const NAV = [
  ["/admin", "Overview", LayoutDashboard], ["/admin/products", "Products", Package], ["/admin/orders", "Orders", ShoppingCart],
  ["/admin/customers", "Customers", Users], ["/admin/promotions", "Promotions", Tag], ["/admin/delivery", "Delivery", Truck],
  ["/admin/inbox", "Inbox", Inbox], ["/admin/settings", "Settings", Settings],
];

export function AdminLogin() {
  const { user } = useStore();
  if (user && user.role === "admin") return <Navigate to="/admin" replace />;
  return (
    <div className="min-h-screen grid place-items-center p-4 bg-[#0a0a0c]">
      <Seo title="Admin Login" />
      <div className="w-full max-w-md flex flex-col items-center gap-6">
        <img src="/logo-512.webp" alt="Mwezzy Footwear" className="w-32 h-32" />
        <h1 className="font-display text-5xl">Admin Login</h1>
        {user && user.role !== "admin" && <p className="text-sm text-red-400">This account does not have admin access.</p>}
        <AuthForm admin />
      </div>
    </div>
  );
}

export default function AdminLayout() {
  const { user, setUser } = useStore();
  if (user === null) return <div className="p-10 text-zinc-400">Loading...</div>;
  if (!user || user.role !== "admin") return <Navigate to="/admin/login" replace />;
  const logout = async () => { await api.post("/auth/logout"); setUser(false); };
  const cls = ({ isActive }) => `flex items-center gap-3 px-4 min-h-[44px] rounded-lg text-sm font-semibold whitespace-nowrap shrink-0 [&>svg]:shrink-0 ${isActive ? "bg-[#d4af37] text-black" : "text-zinc-300 hover:bg-white/5"}`;
  return (
    <div className="min-h-screen bg-[#0a0a0c] lg:grid lg:grid-cols-[240px_1fr]" data-testid="admin-layout">
      <Seo title="Admin Dashboard" />
      <aside className="lg:h-screen lg:sticky lg:top-0 border-b lg:border-b-0 lg:border-r border-white/10 bg-[#070708] p-3 lg:p-5 flex lg:flex-col gap-3">
        <Link to="/admin" className="hidden lg:flex items-center gap-2 mb-4"><img src="/logo-512.webp" alt="" className="w-14 h-14" /><span className="font-display text-xl leading-none">MWEZZY<br /><span className="text-gold text-sm">ADMIN</span></span></Link>
        <nav className="flex lg:flex-col gap-1 overflow-x-auto no-scrollbar flex-1">
          {NAV.map(([to, l, Icon]) => <NavLink key={to} to={to} end={to === "/admin"} className={cls} data-testid={`admin-nav-${l.toLowerCase()}`}><Icon size={18} /> {l}</NavLink>)}
        </nav>
        <div className="hidden lg:flex flex-col gap-1">
          <a href="/" target="_blank" rel="noreferrer" className={cls({})}><Store size={18} /> View store</a>
          <button onClick={logout} className={cls({})} data-testid="admin-logout-button"><LogOut size={18} /> Log out</button>
        </div>
      </aside>
      <main className="p-4 sm:p-8 min-w-0"><Outlet /></main>
    </div>
  );
}

export function AdminTitle({ title, children }) {
  return (
    <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
      <h1 className="font-display text-5xl sm:text-6xl leading-none">{title}</h1>
      <div className="flex gap-2 flex-wrap">{children}</div>
    </div>
  );
}

export const Toggle = ({ label, checked, onChange, testid }) => (
  <label className="flex items-center gap-3 text-sm cursor-pointer min-h-[44px]">
    <input type="checkbox" checked={!!checked} onChange={(e) => onChange(e.target.checked)} className="accent-[#d4af37] w-5 h-5" data-testid={testid} /> {label}
  </label>
);
